import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { Avatar } from "../components/Avatar";
import { FilterPanel } from "../components/FilterPanel";
import { GameCard } from "../components/GameCard";
import { Sheet } from "../components/Sheet";
import { TagSheet } from "../components/TagSheet";
import { Toast } from "../components/Toast";
import {
  activeFilters,
  fetchEvents,
  filtersFromSearch,
  filtersToSearch,
  groupByYear,
  labelFor,
  refreshScores,
  setSolo,
  withFilter,
  type EventRow,
  type EventsDto,
  type FilterKey,
  type FilterOptions,
  type Person,
} from "../lib/eventsPage";
import { games } from "../lib/format";
import AddEventSheet from "./AddEventSheet";

const OPTIONS_FOR: Record<FilterKey, keyof FilterOptions | null> = {
  sport: "sport",
  league: "league",
  team: "team",
  side: null,
  venue: "venue",
  game_type: "game_type",
  special: "special",
  with: "attendee",
  year: "year",
};

/** The active filters as chips a tap removes, plus Clear all. */
export function FilterChips({
  dto,
  filters,
  onChange,
  onClear,
}: {
  dto: Pick<EventsDto, "options">;
  filters: ReturnType<typeof filtersFromSearch>;
  onChange: (k: FilterKey, v: string) => void;
  onClear: () => void;
}) {
  const on = activeFilters(filters);
  if (on.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {on.map((k) => {
        const opts = OPTIONS_FOR[k];
        const label = labelFor(opts ? dto.options[opts] : undefined, filters[k]);
        return (
          <span key={k} className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface py-1 pr-1.5 pl-2.5 text-[13px]">
            <b className="font-semibold">
              {k === "with" ? `With ${label}` : label}
              {k === "team" && filters.side !== "All" ? ` · ${filters.side}` : ""}
            </b>
            <button
              type="button"
              aria-label={`Remove the ${k.replace("_", " ")} filter`}
              onClick={() => onChange(k, "All")}
              className="grid h-6 w-6 place-items-center text-faint hover:text-neutral-100"
            >
              ✕
            </button>
          </span>
        );
      })}
      <button type="button" onClick={onClear} className="rounded-full border border-line px-2.5 py-1 text-[13px] text-muted">
        Clear all
      </button>
    </div>
  );
}

function Who({
  event: e,
  people,
  onTag,
  onUnsolo,
}: {
  event: EventRow;
  people: Map<number, Person>;
  onTag: () => void;
  onUnsolo: () => void;
}) {
  if (e.solo) {
    return (
      <div className="flex items-center gap-2 text-[12.5px] text-faint">
        Went alone
        <button type="button" onClick={onUnsolo} className="text-accent">
          Undo
        </button>
      </div>
    );
  }
  const tagged = e.attendee_ids.map((id) => people.get(id)).filter((p): p is Person => !!p);
  if (tagged.length === 0) {
    return (
      <button type="button" onClick={onTag} className="min-h-8 text-[13px] text-accent">
        ＋ Tag who you went with
      </button>
    );
  }
  return (
    <button type="button" onClick={onTag} aria-label="Edit who went" className="flex flex-wrap gap-1.5 text-left">
      {tagged.map((p) => (
        <span key={p.id} className="inline-flex items-center gap-1.5 rounded-full bg-white/5 py-0.5 pr-2.5 pl-0.5 text-[13px]">
          <Avatar name={p.name} url={p.avatar_url} />
          {p.name}
        </span>
      ))}
    </button>
  );
}

/**
 * Events: the log, newest first, under year headers, filtered by the URL.
 * Filters apply as they change, from a rail on desktop or a sheet on a phone;
 * the server filters and returns the option lists (filters_logic).
 */
export default function Events() {
  const [sp, setSp] = useSearchParams();
  const filters = filtersFromSearch(sp);
  const query = filtersToSearch(filters).toString();
  const [dto, setDto] = useState<EventsDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloads, setReloads] = useState(0);
  const [sheet, setSheet] = useState<"filters" | "add" | number | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchEvents(filtersFromSearch(new URLSearchParams(query)))
      .then((d) => {
        if (cancelled) return;
        setDto(d);
        setError(null);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [query, reloads]);

  const reload = useCallback(() => setReloads((n) => n + 1), []);

  // Scores refresh on their own: once per visit, in the background, only
  // when ESPN has games not yet final; the list reloads only if a score
  // actually changed.
  const refreshed = useRef(false);
  const nonFinal = dto?.non_final ?? 0;
  useEffect(() => {
    if (refreshed.current || nonFinal === 0) return;
    refreshed.current = true;
    refreshScores()
      .then((r) => {
        if (r.updated > 0) reload();
      })
      .catch(() => {
        // A failed refresh leaves the stored scores on screen, which is what
        // there was before; not worth an error over.
      });
  }, [nonFinal, reload]);

  const onChange = (k: FilterKey, v: string) => setSp(filtersToSearch(withFilter(filters, k, v)), { replace: true });
  const onClear = () => setSp(new URLSearchParams(), { replace: true });
  const people = useMemo(() => new Map((dto?.people ?? []).map((p) => [p.id, p])), [dto]);
  const closeToast = useCallback(() => setToast(null), []);

  if (error && !dto) {
    return (
      <div className="space-y-3">
        <div className="rounded-lg border border-bad/50 bg-bad/10 p-4 text-sm">Could not load events: {error}</div>
        <button type="button" onClick={reload} className="rounded-lg bg-neutral-100 px-3.5 py-2 text-sm font-semibold text-ground">
          Try again
        </button>
      </div>
    );
  }
  if (!dto) return <p className="text-sm text-muted">Loading…</p>;

  const n = activeFilters(filters).length;
  const tagging = typeof sheet === "number" ? dto.events.find((e) => e.id === sheet) : undefined;

  return (
    <section className="grid gap-4">
      <div className="flex flex-wrap items-baseline gap-2.5">
        <h1 className="font-display text-3xl leading-none font-bold uppercase">Events</h1>
        <span className="text-sm text-muted tabular-nums">
          {dto.events.length} of {dto.total}
        </span>
        <div className="ml-auto flex gap-2">
          <button
            type="button"
            onClick={() => setSheet("filters")}
            className="min-h-10 rounded-lg border border-line px-3 text-sm md:hidden"
          >
            Filters
            {n > 0 && (
              <span className="ml-1.5 inline-grid h-4.5 min-w-4.5 place-items-center rounded-full bg-gold px-1 text-[11px] font-bold text-ground">
                {n}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setSheet("add")}
            className="min-h-10 rounded-lg bg-neutral-100 px-3.5 text-sm font-semibold text-ground"
          >
            ＋ Add
          </button>
        </div>
      </div>

      <FilterChips dto={dto} filters={filters} onChange={onChange} onClear={onClear} />
      {error && <p className="text-sm text-bad">Could not refresh: {error}</p>}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-[230px_minmax(0,1fr)] md:items-start">
        <aside className="hidden md:block">
          <FilterPanel options={dto.options} filters={filters} onChange={onChange} />
        </aside>
        <div className="grid gap-2">
          {dto.events.length === 0 && <p className="text-sm text-muted">No games match these filters.</p>}
          {groupByYear(dto.events).map((g) => (
            <section key={g.year}>
              <div className="sticky top-0 z-10 flex items-baseline gap-2.5 bg-ground py-2.5">
                <h2 className="font-display text-2xl leading-none font-bold">{g.year}</h2>
                <span className="text-[13px] text-faint">{games(g.events.length)}</span>
              </div>
              <div className="grid gap-2.5">
                {g.events.map((e) => (
                  <GameCard
                    key={e.id}
                    game={e}
                    logos={dto.logos}
                    venueImages={dto.venue_images}
                    footer={
                      <Who
                        event={e}
                        people={people}
                        onTag={() => setSheet(e.id)}
                        onUnsolo={() =>
                          setSolo(e.id, false)
                            .then(reload)
                            .catch((err: unknown) => setToast(err instanceof Error ? err.message : String(err)))
                        }
                      />
                    }
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>

      {sheet === "filters" && (
        <Sheet title="Filters" onClose={() => setSheet(null)}>
          <div className="grid gap-4">
            <FilterPanel options={dto.options} filters={filters} onChange={onChange} />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClear} className="min-h-10 rounded-lg border border-line px-3.5 text-sm">
                Clear all
              </button>
              <button
                type="button"
                onClick={() => setSheet(null)}
                className="min-h-10 rounded-lg bg-neutral-100 px-3.5 text-sm font-semibold text-ground"
              >
                Show {games(dto.events.length)}
              </button>
            </div>
          </div>
        </Sheet>
      )}
      {sheet === "add" && (
        <AddEventSheet
          onClose={() => setSheet(null)}
          onSaved={() => {
            setSheet(null);
            setToast("Event saved");
            reload();
          }}
        />
      )}
      {tagging && (
        <TagSheet
          event={tagging}
          people={dto.people}
          onClose={() => setSheet(null)}
          onSaved={(m) => {
            setSheet(null);
            setToast(m);
            reload();
          }}
        />
      )}
      {toast && <Toast text={toast} onDone={closeToast} />}
    </section>
  );
}
